import { ComponentProps, forwardRef } from 'react';
import { Css, CancelIconLight } from '@/ui';
import styled from '@emotion/styled';
import SearchIcon from '../../../assets/icons/search.svg';
import { Color } from '../../../styles/Color';
import { FONT_FAMILY_INTER } from '../../../styles/Font';

const InputWrapper = styled.div`
	position: relative;
	width: 100%;
	overflow: hidden;
`;

const StyledInput = styled.input`
	font-family: ${FONT_FAMILY_INTER};
	border-radius: 8px;
	padding: ${Css.pxToRem(28)} ${Css.pxToRem(56)} ${Css.pxToRem(8)};
	background: ${Color.Neutral94};
	font-size: ${Css.pxToRem(16)};
	width: 100%;
	border: none;
	outline: none;
	border-bottom: 2px solid transparent;

	&::placeholder {
		opacity: 0;
	}

	&:focus {
		border-color: ${Color.PrimaryBlue};
	}
`;

const IconWrapper = styled.div`
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	padding: ${Css.pxToRem(16)};
	pointer-events: none;
	display: flex;
	align-items: center;

	svg path {
		fill: currentColor;
	}
`;

const ClearWrapper = styled.div`
	position: absolute;
	right: 0;
	top: 0;
	bottom: 0;
	padding: ${Css.pxToRem(16)};
	pointer-events: none;
	align-items: center;
	display: none;

	input:not(:placeholder-shown) ~ & {
		display: flex;
	}
`;

const ClearButton = styled.button`
	pointer-events: all;
	border: none;
	padding: 0;
	margin: 0;
	cursor: pointer;
	border-radius: 50%;
	padding: ${Css.pxToRem(4)};
	transition: background 0.15s ease-in-out;

	&:hover {
		background: ${Color.Neutral80};
	}
`;

const Label = styled.label`
	position: absolute;
	top: 0;
	right: ${Css.pxToRem(56)};
	left: ${Css.pxToRem(56)};
	height: 100%;
	font-size: ${Css.pxToRem(16)};
	font-weight: 400;
	display: flex;
	align-items: center;
	transition: all 0.15s ease-in-out;
	transform-origin: top left;
	pointer-events: none;
	overflow: hidden;

	input:focus ~ & {
		color: ${Color.PrimaryBlue};
	}

	input:focus ~ &,
	input:not(:placeholder-shown) ~ & {
		font-size: ${Css.pxToRem(14)};
		height: 60%;
	}
`;

const LabelTruncate = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
`;

interface IProps extends Omit<ComponentProps<'input'>, 'placeholder'> {
	label: string;
	labelId?: string;
	onClear: () => void;
}

export const SearchInput = forwardRef<HTMLInputElement, IProps>(
	({ label, labelId, onClear, id, ...inputProps }, ref) => (
		<InputWrapper>
			<StyledInput
				ref={ref}
				type="text"
				role="search"
				id={id}
				aria-labelledby={labelId}
				placeholder={label}
				{...inputProps}
			/>
			<Label htmlFor={id} id={labelId}>
				<LabelTruncate>{label}</LabelTruncate>
			</Label>
			<IconWrapper>
				<SearchIcon width={24} height={24} />
			</IconWrapper>
			<ClearWrapper>
				<ClearButton
					type="button"
					tabIndex={-1}
					aria-label="Clear search"
					onClick={onClear}
				>
					<CancelIconLight />
				</ClearButton>
			</ClearWrapper>
		</InputWrapper>
	),
);

SearchInput.displayName = 'SearchInput';
